import { supabase } from './supabase';

// Match key for identifying the "same item" across invoices
function itemKey(item) {
  const num = (item.item_number || '').trim();
  if (num) return num;
  return (item.description || '').trim().toLowerCase();
}

export async function checkInvoiceForAnomalies({ invoiceId, vendor, orgId, lineItems }) {
  // 1. Check vendor monitoring mode
  const { data: vendorRow } = await supabase
    .from('vendors')
    .select('monitoring_mode, vendor_category')
    .eq('org_id', orgId)
    .ilike('name', vendor)
    .limit(1)
    .single();

  const mode = vendorRow?.monitoring_mode || 'auto';
  if (mode === 'force_off') return { anomalies: [], qualified: false, reason: 'forced_off' };
  const skipNewMissing = (vendorRow?.vendor_category || 'inventory') !== 'expense';

  // 2. Auto-qualification: 3+ invoices in 60 days
  if (mode === 'auto') {
    const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const { count } = await supabase
      .from('invoices')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('vendor', vendor)
      .gte('invoice_date', sixtyDaysAgo);
    if ((count || 0) < 3) return { anomalies: [], qualified: false, reason: 'auto_not_enough_invoices' };
  }

  // 3. Get last 3 prior invoices (excluding the new one)
  const { data: priorInvoices } = await supabase
    .from('invoices')
    .select('id, invoice_date')
    .eq('org_id', orgId)
    .eq('vendor', vendor)
    .neq('id', invoiceId)
    .order('invoice_date', { ascending: false })
    .limit(3);

  if (!priorInvoices?.length) return { anomalies: [], qualified: false, reason: 'no_history' };

  // 4. Fetch line items for prior invoices
  const priorIds = priorInvoices.map(i => i.id);
  const { data: priorItems } = await supabase
    .from('invoice_line_items')
    .select('invoice_id, item_number, description, qty_shipped, unit_price, amount, catch_weight')
    .in('invoice_id', priorIds);

  if (!priorItems?.length) return { anomalies: [], qualified: true, reason: 'no_prior_line_items' };

  // 5. Build recurring items map
  // Group prior items by key, track which invoices they appear on
  const priorMap = new Map(); // key -> { invoiceIds: Set, latest: { price, qty, date } }
  const invoiceDateMap = {};
  priorInvoices.forEach(inv => { invoiceDateMap[inv.id] = inv.invoice_date; });

  for (const item of priorItems) {
    const key = itemKey(item);
    if (!priorMap.has(key)) priorMap.set(key, { invoiceIds: new Set(), latest: null, item_number: '', description: '' });
    const entry = priorMap.get(key);
    entry.invoiceIds.add(item.invoice_id);
    entry.item_number = item.item_number || entry.item_number;
    entry.description = item.description || entry.description;
    // Track most recent occurrence
    const itemDate = invoiceDateMap[item.invoice_id] || '';
    if (!entry.latest || itemDate > entry.latest.date) {
      entry.latest = { price: item.unit_price, qty: item.qty_shipped, date: itemDate, amount: item.amount };
    }
  }

  // Recurring = appears on 2+ of the prior invoices
  const recurring = new Map();
  for (const [key, entry] of priorMap) {
    if (entry.invoiceIds.size >= 2) recurring.set(key, entry);
  }

  // 6. Compare new line items
  const anomalies = [];
  const newKeys = new Set();

  for (const item of lineItems) {
    const key = itemKey(item);
    newKeys.add(key);

    // Math mismatch — use catch_weight when present, qty_shipped otherwise
    if (item.qty_shipped && item.unit_price) {
      const multiplier = item.catch_weight != null ? item.catch_weight : item.qty_shipped;
      const lineTax = item.line_tax != null ? item.line_tax : 0;
      const expected = (multiplier * item.unit_price) + lineTax;
      if (Math.abs(expected - (item.amount || 0)) > 0.50) {
        anomalies.push({ type: 'math_mismatch', item_number: item.item_number || '', description: item.description || '', old_value: Math.round(expected * 100) / 100, new_value: item.amount || 0, prior_invoice_date: null, pct_change: null });
      }
    }

    const rec = recurring.get(key);
    if (!rec) {
      // New item (only flag for expense vendors with 2+ prior invoices)
      if (!skipNewMissing && priorInvoices.length >= 2) {
        anomalies.push({
          type: 'new_item',
          item_number: item.item_number || '',
          description: item.description || '',
          old_value: null,
          new_value: item.amount || 0,
          prior_invoice_date: null,
          pct_change: null
        });
      }
      continue;
    }

    // Price change (>2.5% threshold)
    if (rec.latest.price > 0 && item.unit_price > 0) {
      const pctChange = (item.unit_price - rec.latest.price) / rec.latest.price * 100;
      if (Math.abs(pctChange) > 2.5) {
        anomalies.push({
          type: 'price_change',
          item_number: item.item_number || '',
          description: item.description || '',
          old_value: rec.latest.price,
          new_value: item.unit_price,
          prior_invoice_date: rec.latest.date,
          pct_change: Math.round(pctChange * 10) / 10
        });
      }
    }

    // Quantity change (expense vendors only)
    if (!skipNewMissing && rec.latest.qty != null && item.qty_shipped != null && rec.latest.qty !== item.qty_shipped) {
      anomalies.push({
        type: 'quantity_change',
        item_number: item.item_number || '',
        description: item.description || '',
        old_value: rec.latest.qty,
        new_value: item.qty_shipped,
        prior_invoice_date: rec.latest.date,
        pct_change: null
      });
    }
  }

  // Missing items: recurring items not on the new invoice (expense vendors only)
  if (!skipNewMissing) {
    for (const [key, entry] of recurring) {
      if (!newKeys.has(key)) {
        anomalies.push({
          type: 'missing_item',
          item_number: entry.item_number || '',
          description: entry.description || '',
          old_value: entry.latest.amount,
          new_value: null,
          prior_invoice_date: entry.latest.date,
          pct_change: null
        });
      }
    }
  }

  if (anomalies.length === 0) return { anomalies: [], qualified: true, reason: 'no_anomalies' };
  return { anomalies, qualified: true };
}

export async function spawnAnomalyTask({ invoiceId, vendor, anomalies, orgId, userId }) {
  // Dedupe: check for existing pending anomaly tasks for same vendor with overlapping items
  const { data: existingTasks } = await supabase
    .from('tasks')
    .select('id, payload')
    .eq('org_id', orgId)
    .eq('type', 'invoice_anomaly')
    .eq('status', 'pending')
    .limit(50);

  // Build set of existing anomaly keys from pending tasks
  const existingKeys = new Set();
  if (existingTasks?.length) {
    for (const task of existingTasks) {
      const taskAnomalies = task.payload?.anomalies || [];
      for (const a of taskAnomalies) {
        // Key: type + item identifier + direction of change (for prices)
        const itemId = a.item_number || a.description || '';
        if (a.type === 'price_change') {
          existingKeys.add(`${a.type}:${itemId}:${a.old_value}:${a.new_value}`);
        } else {
          existingKeys.add(`${a.type}:${itemId}`);
        }
      }
    }
  }

  // Filter out anomalies that already have a pending task
  const novel = anomalies.filter(a => {
    const itemId = a.item_number || a.description || '';
    if (a.type === 'price_change') {
      return !existingKeys.has(`${a.type}:${itemId}:${a.old_value}:${a.new_value}`);
    }
    return !existingKeys.has(`${a.type}:${itemId}`);
  });

  if (novel.length === 0) return; // All anomalies already have pending tasks

  // Build summary note
  const counts = {};
  novel.forEach(a => { counts[a.type] = (counts[a.type] || 0) + 1; });
  const parts = [];
  if (counts.price_change) parts.push(`${counts.price_change} price change${counts.price_change > 1 ? 's' : ''}`);
  if (counts.quantity_change) parts.push(`${counts.quantity_change} qty change${counts.quantity_change > 1 ? 's' : ''}`);
  if (counts.new_item) parts.push(`${counts.new_item} new item${counts.new_item > 1 ? 's' : ''}`);
  if (counts.missing_item) parts.push(`${counts.missing_item} missing item${counts.missing_item > 1 ? 's' : ''}`);
  if (counts.math_mismatch) parts.push(`${counts.math_mismatch} math error${counts.math_mismatch > 1 ? 's' : ''}`);

  await supabase.from('tasks').insert({
    org_id: orgId,
    type: 'invoice_anomaly',
    status: 'pending',
    title: `Review: ${vendor} invoice — ${novel.length} ${novel.length === 1 ? 'flag' : 'flags'}`,
    note: parts.join(', '),
    target_page: 'tasks',
    target_id: invoiceId,
    source: 'system',
    priority: 5,
    payload: { invoice_id: invoiceId, vendor, anomalies: novel, generated_at: new Date().toISOString() }
  });
}
